export type ConsentValue = 'granted' | 'denied';
export type ConsentChoice = 'all' | 'essential' | 'custom';

export type ConsentState = {
  choice: ConsentChoice | null;
  region: 'regulated' | 'nonregulated' | 'unknown';
  ad_storage: ConsentValue;
  analytics_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
};

export const CONSENT_STORAGE_KEY = 'solargabay.consent.v2';
export const CONSENT_EVENT = 'solargabay:consent-change';

const deniedState: Omit<ConsentState, 'choice' | 'region'> = {
  ad_storage: 'denied',
  analytics_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

export function getConsentState(): ConsentState {
  if (typeof window === 'undefined') return { choice: null, region: 'unknown', ...deniedState };
  const bootstrap = window.__SOLARGABAY_CONSENT__;
  return bootstrap ?? { choice: null, region: 'unknown', ...deniedState };
}

export function updateConsent(
  values: Pick<
    ConsentState,
    'ad_storage' | 'analytics_storage' | 'ad_user_data' | 'ad_personalization'
  >,
  choice: ConsentChoice,
) {
  const previous = getConsentState();
  const next: ConsentState = { ...previous, ...values, choice };
  window.__SOLARGABAY_CONSENT__ = next;
  try {
    localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({ version: 2, choice, values, updatedAt: new Date().toISOString() }),
    );
  } catch {
    // Consent still applies for this page when storage is unavailable.
  }
  window.gtag?.('consent', 'update', values);
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: next }));
}

export const grantAllConsent = () =>
  updateConsent(
    {
      ad_storage: 'granted',
      analytics_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    },
    'all',
  );

export const rejectNonessentialConsent = () => updateConsent(deniedState, 'essential');

export function advertisingConsentGranted() {
  const state = getConsentState();
  return state.ad_storage === 'granted' && state.ad_user_data === 'granted';
}
